/**
 * CustomFieldsPanel
 *
 * Formulário de campos personalizados em ABAS (espelha a aba "Principal" do
 * Kommo) para um contato. Carrega schema + valores via customFieldsService,
 * renderiza inputs TIPADOS por aba e salva em merge (PUT) no blur/alteração.
 *
 * Funciona nos 2 inboxes — a chave é o telefone normalizado; o backend faz o
 * fan-out para WA Business e WA Personal.
 *
 * Props:
 *   - colors      -> theme (useTheme)
 *   - phone       -> telefone do contato
 *   - compact     -> (opcional) reduz paddings p/ caber no drawer
 *   - hideHeader  -> (opcional) suprime o cabeçalho interno (quando embrulhado
 *                    por uma seção colapsável que já tem título)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Check, AlertCircle, SlidersHorizontal } from 'lucide-react';
import customFieldsService from '../../services/global/customFieldsService';
import { formatBRL, parseBRL } from '../../utils/currency';

// F3 (stale-while-revalidate): o SCHEMA (abas + definições) é do workspace e
// igual para todos os contatos; só os VALORES mudam por contato. Guardamos o
// último schema visto em memória para a ESTRUTURA do painel pintar na hora ao
// reabrir/trocar de contato (sem spinner travando tudo), enquanto o fetch
// revalida schema+valores. Vive enquanto a aba estiver aberta (reset no reload).
let cachedSchema = null;

// onValuesChange(values, definitions) (opcional): avisa o pai a cada mudança
// local de valor/schema. Usado pelo criar contato para conferir obrigatórios.
const CustomFieldsPanel = ({ colors, phone, compact = false, hideHeader = false, onValuesChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [values, setValues] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    // F3: com o schema em cache, a estrutura (abas/campos) aparece na hora e os
    // VALORES resetam aqui (são por-contato) — chegam pelo fetch abaixo, que
    // também revalida o schema. Sem cache (1ª abertura), mantém o spinner.
    if (cachedSchema) {
      setTabs(cachedSchema.tabs);
      setDefinitions(cachedSchema.definitions);
      setActiveTab((prev) => prev || (cachedSchema.tabs[0] && cachedSchema.tabs[0].id) || null);
      setValues({});
    } else {
      setLoading(true);
    }
    try {
      // Sem telefone (criar-lead): carrega só a ESTRUTURA (abas/campos) do
      // workspace; os VALORES chegam depois que o número é digitado.
      const data = phone
        ? await customFieldsService.getContactFields(phone)
        : { ...(await customFieldsService.getSchema()), values: {} };
      const sortedTabs = (data.tabs || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setTabs(sortedTabs);
      setDefinitions(data.definitions || []);
      setValues(data.values || {});
      setActiveTab((prev) => prev || (sortedTabs[0] && sortedTabs[0].id) || null);
      // Revalida o cache do schema para as próximas aberturas.
      cachedSchema = { tabs: sortedTabs, definitions: data.definitions || [] };
    } catch (err) {
      console.error('[CustomFieldsPanel] load error:', err);
      setError('Não foi possível carregar os campos.');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => { load(); }, [load]);

  const onValuesChangeRef = React.useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;
  useEffect(() => {
    if (onValuesChangeRef.current) onValuesChangeRef.current(values, definitions);
  }, [values, definitions]);

  const persist = async (definitionId, value) => {
    if (!phone) return;  // sem telefone ainda (criar-lead): edite após digitar o número
    setSavingId(definitionId);
    setSavedId(null);
    try {
      await customFieldsService.setContactFields(phone, { [definitionId]: value });
      setSavedId(definitionId);
      setTimeout(() => setSavedId((cur) => (cur === definitionId ? null : cur)), 1500);
    } catch (err) {
      console.error('[CustomFieldsPanel] save error:', err);
      setError(err?.response?.data?.detail || 'Erro ao salvar o campo.');
    } finally {
      setSavingId((cur) => (cur === definitionId ? null : cur));
    }
  };

  const setLocal = (definitionId, value) => {
    setValues((prev) => ({ ...prev, [definitionId]: value }));
  };

  const tabFields = definitions
    .filter((d) => d.tab_id === activeTab && !d.api_only)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const pad = compact ? '16px 20px' : '20px 24px';

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8,
        padding: 20, color: colors.textSecondary, fontSize: 12,
      }}>
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Carregando…
      </div>
    );
  }

  return (
    <section style={{ padding: hideHeader ? 0 : pad }}>
      {!hideHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <SlidersHorizontal size={16} color={colors.accentPrimary} />
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>
            Campos personalizados
          </h4>
        </div>
      )}

      {definitions.length === 0 ? (
        <div style={{
          fontSize: 12, fontStyle: 'italic', color: colors.textTertiary,
          textAlign: 'center', padding: 8,
        }}>
          Nenhum campo configurado. Crie campos em Configurar campos.
        </div>
      ) : (
        <>
          {/* Abas */}
          {tabs.length > 1 && (
            <div style={{
              display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap',
              borderBottom: `1px solid ${colors.borderColor}`, paddingBottom: 8,
            }}>
              {tabs.map((tab) => {
                const isActive = tab.id === activeTab;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding: '5px 12px', fontSize: 12, fontWeight: isActive ? 600 : 500,
                      borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${isActive ? colors.accentPrimary : colors.borderColor}`,
                      backgroundColor: isActive ? `${colors.accentPrimary}14` : 'transparent',
                      color: isActive ? colors.accentPrimary : colors.textSecondary,
                    }}
                  >
                    {tab.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Campos da aba ativa */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {tabFields.map((def) => (
              <FieldRow
                key={def.id}
                def={def}
                colors={colors}
                value={values[def.id]}
                saving={savingId === def.id}
                saved={savedId === def.id}
                onLocalChange={(v) => setLocal(def.id, v)}
                onCommit={(v) => persist(def.id, v)}
              />
            ))}
            {tabFields.length === 0 && (
              <div style={{
                fontSize: 12, fontStyle: 'italic', color: colors.textTertiary,
                textAlign: 'center', padding: 8,
              }}>
                Nenhum campo nesta aba.
              </div>
            )}
          </div>
        </>
      )}

      {error && (
        <div style={{
          marginTop: 10, fontSize: 11, color: '#DC2626',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <AlertCircle size={12} /> {error}
        </div>
      )}
    </section>
  );
};

/**
 * Uma linha label + input tipado. Mantém o estado local (digitação) e dispara
 * onCommit no blur/seleção para salvar (merge) no backend.
 */
const FieldRow = ({ def, colors, value, saving, saved, onLocalChange, onCommit }) => {
  // Moeda: enquanto foca digita livre; no blur converte e volta formatado.
  const [currencyFocus, setCurrencyFocus] = useState(false);
  const labelEl = (
    <label style={{
      fontSize: 12, fontWeight: 500, color: colors.textSecondary,
      display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
    }}>
      {def.name}
      {saving && <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />}
      {saved && <Check size={12} color={colors.accentPrimary} />}
    </label>
  );

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    border: `1px solid ${colors.borderColor}`, borderRadius: 8,
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };

  const renderInput = () => {
    switch (def.type) {
      case 'textarea':
        return (
          <textarea
            rows={3}
            value={value || ''}
            onChange={(e) => onLocalChange(e.target.value)}
            onBlur={(e) => onCommit(e.target.value)}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => onLocalChange(e.target.value)}
            onBlur={(e) => onCommit(e.target.value === '' ? null : Number(e.target.value))}
            style={inputStyle}
          />
        );
      case 'currency':
        return (
          <input
            type="text"
            inputMode="decimal"
            placeholder="R$ 0"
            value={currencyFocus ? (value ?? '') : formatBRL(value)}
            onFocus={() => setCurrencyFocus(true)}
            onChange={(e) => onLocalChange(e.target.value)}
            onBlur={(e) => {
              setCurrencyFocus(false);
              const num = parseBRL(e.target.value);
              onLocalChange(num);
              onCommit(num);
            }}
            style={inputStyle}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => { onLocalChange(e.target.value); onCommit(e.target.value || null); }}
            style={inputStyle}
          />
        );
      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value || ''}
            onChange={(e) => { onLocalChange(e.target.value); onCommit(e.target.value || null); }}
            style={inputStyle}
          />
        );
      case 'checkbox':
        return (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => { onLocalChange(e.target.checked); onCommit(e.target.checked); }}
              style={{ width: 16, height: 16, accentColor: colors.accentPrimary }}
            />
            <span style={{ fontSize: 13, color: colors.textPrimary }}>
              {value ? 'Sim' : 'Não'}
            </span>
          </label>
        );
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => { onLocalChange(e.target.value); onCommit(e.target.value || null); }}
            style={inputStyle}
          >
            <option value="">—</option>
            {(def.options || []).map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        );
      case 'multiselect': {
        const selected = Array.isArray(value) ? value : [];
        const toggle = (optId) => {
          const next = selected.includes(optId)
            ? selected.filter((id) => id !== optId)
            : [...selected, optId];
          onLocalChange(next);
          onCommit(next);
        };
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(def.options || []).map((opt) => {
              const on = selected.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  onClick={() => toggle(opt.id)}
                  style={{
                    padding: '5px 10px', fontSize: 12, borderRadius: 16, cursor: 'pointer',
                    border: `1px solid ${on ? colors.accentPrimary : colors.borderColor}`,
                    backgroundColor: on ? `${colors.accentPrimary}1A` : 'transparent',
                    color: on ? colors.accentPrimary : colors.textSecondary,
                    fontWeight: on ? 600 : 500,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );
      }
      default: // text
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onLocalChange(e.target.value)}
            onBlur={(e) => onCommit(e.target.value)}
            style={inputStyle}
          />
        );
    }
  };

  return (
    <div>
      {labelEl}
      {renderInput()}
    </div>
  );
};

// FieldRow reaproveitado pelo popup de obrigatórios (RequiredFieldsPrompt).
export { FieldRow };
export default CustomFieldsPanel;
